import { SearchableSelect } from "@/components/ui/searchable-select";
import { useApiSWR } from "@/hooks/useApiSWR";
import type { EmailToTaskFormApi } from "@/hooks/useEmailToTaskForm";
import { listProjects } from "@/services/project.service";
import type {
	GroupingMode,
	NestedListMode,
	TaskPriority,
} from "@/services/email-to-task.service";
import { SectionBlock } from "../SectionBlock";
import { Field, ToggleRow } from "./fields";

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
	{ value: "low", label: "Low" },
	{ value: "medium", label: "Medium" },
	{ value: "high", label: "High" },
	{ value: "urgent", label: "Urgent" },
];

const GROUPING_OPTIONS: { value: GroupingMode; label: string; description: string }[] = [
	{ value: "person_topic", label: "One parent per person and topic", description: "\"@Joel / AI Agents\" becomes one parent task" },
	{ value: "per_email", label: "One parent per email", description: "All items become subtasks of one task" },
];

const NESTED_OPTIONS: { value: NestedListMode; label: string }[] = [
	{ value: "checklist", label: "Checklist items" },
	{ value: "subtasks", label: "Separate subtasks" },
];

export function TaskDefaultsCard({ form, setField, disabled }: EmailToTaskFormApi) {
	const { data: projects, isLoading } = useApiSWR("email-to-task:projects", () => listProjects(), {
		revalidateOnFocus: false,
	});
	const projectOptions = (projects ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.key})` }));

	return (
		<SectionBlock
			title="Task defaults"
			description="How drafts are shaped and where tasks land when the email does not say."
		>
			<div className="grid gap-4 sm:grid-cols-2">
				<Field label="Default project" hint="Used when the agent cannot match a project.">
					<SearchableSelect
						value={form.default_project_id}
						onValueChange={(v) => setField("default_project_id", v || null)}
						options={projectOptions}
						placeholder="Select a project"
						loading={isLoading}
						disabled={disabled}
						ariaLabel="Default project"
					/>
				</Field>
				<Field label="Default priority">
					<SearchableSelect
						value={form.default_priority}
						onValueChange={(v) => setField("default_priority", v as TaskPriority)}
						options={PRIORITY_OPTIONS}
						disabled={disabled}
						ariaLabel="Default priority"
					/>
				</Field>
				<Field label="Grouping">
					<SearchableSelect
						value={form.grouping_mode}
						onValueChange={(v) => setField("grouping_mode", v as GroupingMode)}
						options={GROUPING_OPTIONS}
						disabled={disabled}
						ariaLabel="Grouping"
					/>
				</Field>
				<Field label="Nested bullet lists become">
					<SearchableSelect
						value={form.nested_list_mode}
						onValueChange={(v) => setField("nested_list_mode", v as NestedListMode)}
						options={NESTED_OPTIONS}
						disabled={disabled}
						ariaLabel="Nested bullet lists become"
					/>
				</Field>
			</div>
			<div className="mt-3 divide-y divide-border">
				<ToggleRow
					label="Add new tasks to the active sprint"
					checked={form.assign_active_sprint}
					onChange={(v) => setField("assign_active_sprint", v)}
					disabled={disabled}
				/>
				<ToggleRow
					label="Resolve due dates from the text"
					description={`"By Friday" is resolved against the email's sent date.`}
					checked={form.resolve_due_dates}
					onChange={(v) => setField("resolve_due_dates", v)}
					disabled={disabled}
				/>
			</div>
		</SectionBlock>
	);
}
